/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ImageGrid from "./BodyImageGrid";
import { fetchData, showAlert } from "../../../../generalUtils";
import { useLoading } from "../../../../Components/Common/LoadingSpinner/LoadingContext";
import { useAppContext } from "../../../../AppContext";
import "react-toastify/dist/ReactToastify.css";
import "./SwingieBodyGallery.css";
import { SERVER_URL } from "../../../../config/serverUrl";
import { getEventScope, isCurrentScope } from "../../../../eventIdentity";
import {
  getImageBlobFromDB,
  getLastUpdateTime,
  updateLastUpdateTime,
  getAllKeysFromDB,
  deleteImageFromDB,
  BODY_STORE_NAME,
} from "../../../../IndexedDBUtils";
import {
  fetchImageBatchWithRetry,
  saveImageBatchToDB,
} from "../../shared/imageBatch";
import FlowCountdown from "../../shared/FlowCountdown";

// Time in seconds before refreshing body images from server
const BODY_REFRESH_INTERVAL_SECONDS = 30;

// One /api/bodies entry as { path, thumb }. Current servers list objects that
// carry the thumbnail ({ path, base64, width, height, ... }); older ones list
// bare paths (thumb null). null when the entry names no usable path.
const normalizeBodyEntry = (entry) => {
  if (!entry) return null;
  if (typeof entry === "string") {
    return entry.length > 0 ? { path: entry, thumb: null } : null;
  }
  if (typeof entry !== "object") return null;
  const path = [entry.path, entry.path_display, entry.path_lower].find(
    (candidate) => typeof candidate === "string" && candidate.length > 0
  );
  if (!path) return null;
  const thumb =
    typeof entry.base64 === "string" && entry.base64.length > 0
      ? { base64: entry.base64, width: entry.width, height: entry.height }
      : null;
  return { path, thumb };
};

const SwingieBodyGalleryPage = () => {
  const serverUrl = SERVER_URL;

  const {
    eventName,
    images,
    setImages,
    selectedImages,
    setSelectedImages,
    totalValue,
    setTotalValue,
  } = useAppContext();

  const number = parseInt(sessionStorage.getItem("number")) || 1;
  const navigate = useNavigate();
  const { showLoading, hideLoading, acquireLoading } = useLoading();
  const unmountedRef = useRef(false);
  const goNextReleaseRef = useRef(null);

  useEffect(() => {
    // Everything this load does after an await may touch SHARED state (the
    // context image list, the shared loading overlay, IndexedDB freshness), so
    // it only does so while this page is mounted AND still in the event visit
    // it was opened for. A late response from event A (or from a page the
    // guest already left) is dropped instead of overwriting B's state.
    const scope = getEventScope();
    let cancelled = false;
    const isCurrent = () => !cancelled && isCurrentScope(scope);
    let releaseFetchLoading = null;
    const hideIfCurrent = () => {
      if (isCurrent()) hideLoading();
    };

    const cleanupPreviousBodyImages = () => {
      const bodyUrls = JSON.parse(
        sessionStorage.getItem("bodyImageBlobUrls") || "{}"
      );
      Object.values(bodyUrls).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
      sessionStorage.removeItem("bodyImageBlobUrls");
    };

    const loadImages = async () => {
      const lastUpdateTime = await getLastUpdateTime(
        eventName,
        BODY_STORE_NAME
      );
      if (!isCurrent()) return;
      const currentTime = new Date();
      const timeDifference = (currentTime - new Date(lastUpdateTime)) / 1000;

      if (timeDifference < BODY_REFRESH_INTERVAL_SECONDS) {
        await loadImagesFromDB();
      } else {
        await fetchDataFromServer();
      }
    };

    // Every saved body as { path, url: blob URL }. null when this visit ended
    // mid-read (the URLs minted so far are revoked). A key whose entry holds
    // no image is dropped from the store, so the next sync lists it as
    // missing and stores it again instead of leaving a hole for good.
    const readImagesFromDB = async () => {
      const dbImages = [];
      const unusable = [];
      const allKeys = await getAllKeysFromDB(eventName, BODY_STORE_NAME);
      if (!isCurrent()) return null;

      for (const path of allKeys) {
        const imageBlob = await getImageBlobFromDB(
          eventName,
          BODY_STORE_NAME,
          path
        );
        if (!isCurrent()) {
          dbImages.forEach(({ url }) => URL.revokeObjectURL(url));
          return null;
        }
        if (imageBlob) {
          const imageUrl = URL.createObjectURL(imageBlob);
          dbImages.push({ path, url: imageUrl });
        } else {
          unusable.push(path);
        }
      }
      for (const path of unusable) {
        if (!isCurrent()) break;
        try {
          await deleteImageFromDB(eventName, BODY_STORE_NAME, path);
        } catch (error) {
          console.error(`Error dropping unusable body ${path}:`, error);
        }
      }
      return dbImages;
    };

    // Show what IndexedDB holds, nothing more (no server fallback). Resolves to
    // the list shown, or null when this visit ended.
    const showStoredBodies = async () => {
      const dbImages = await readImagesFromDB();
      if (!dbImages) return null;
      setImages(dbImages);
      return dbImages;
    };

    const loadImagesFromDB = async () => {
      const dbImages = await showStoredBodies();
      if (!dbImages) return;

      if (dbImages.length === 0) {
        fetchDataFromServer();
      }
      hideIfCurrent();
    };

    // Bodies missing from IndexedDB never cost one request each: ~127
    // parallel thumbnail GETs held every Redis connection on 2026-10-06, and
    // per-file fetches after a shed batch would turn one refused request into
    // a hundred on the very lane that refused it.
    //  1. /api/bodies already carries each body's thumbnail on current
    //     servers, so those bodies are stored with no further request.
    //  2. Whatever is still missing (an older server lists bare paths) comes
    //     from ONE /api/images/batch call (the payload Home stores), retried
    //     once after the server's Retry-After when it was shed (503 "busy" or
    //     "cache_unavailable") or the network failed.
    //  3. Nothing is fetched per file. A body still missing stays out of the
    //     grid until a later open stores it.
    // Resolves true when every missing body is now stored.
    const storeMissingBodies = async (missingPaths, listedThumbs) => {
      const stored = new Set();
      const inline = {};
      missingPaths.forEach((path) => {
        if (listedThumbs.has(path)) inline[path] = listedThumbs.get(path);
      });
      if (Object.keys(inline).length > 0) {
        try {
          const saved = await saveImageBatchToDB(
            eventName,
            BODY_STORE_NAME,
            inline,
            { isCurrent }
          );
          saved.forEach((path) => stored.add(path));
        } catch (error) {
          console.error("Error storing the listed bodies:", error);
        }
        if (!isCurrent()) return false;
      }

      const remaining = missingPaths.filter((path) => !stored.has(path));
      if (remaining.length === 0) return true;
      try {
        const imageBatch = await fetchImageBatchWithRetry(
          serverUrl,
          `${eventName}/bodies`,
          { isCurrent }
        );
        if (!isCurrent()) return false;
        const saved = await saveImageBatchToDB(
          eventName,
          BODY_STORE_NAME,
          imageBatch,
          { isCurrent, only: remaining }
        );
        saved.forEach((path) => stored.add(path));
      } catch (error) {
        console.error("Error fetching the bodies batch:", error);
      }
      return missingPaths.every((path) => stored.has(path));
    };

    const fetchDataFromServer = async () => {
      if (eventName) {
        const encodedEventName = encodeURIComponent(eventName);
        // This load's own overlay owner: released here or on unmount, so a
        // cancelled load can neither strand it nor hide anyone else's.
        if (releaseFetchLoading) releaseFetchLoading();
        releaseFetchLoading = acquireLoading();
        const releaseOwn = releaseFetchLoading;
        try {
          // A shed listing (503 busy / cache_unavailable) is sent again once,
          // after the server's Retry-After, never back to back (each listing
          // carries every thumbnail inline, so it is a heavy request), and
          // not at all once this visit has ended.
          const data = await fetchData(
            serverUrl,
            "/api/bodies",
            encodedEventName,
            "GET",
            null,
            3,
            10000,
            { honorRetryAfter: true, isCurrent }
          );
          if (!isCurrent()) return;

          const listed = (Array.isArray(data) ? data : [])
            .map(normalizeBodyEntry)
            .filter(Boolean);
          const normalizedPaths = listed.map(({ path }) => path);
          const listedThumbs = new Map(
            listed
              .filter(({ thumb }) => thumb)
              .map(({ path, thumb }) => [path, thumb])
          );

          const keysFromDB = await getAllKeysFromDB(eventName, BODY_STORE_NAME);
          if (!isCurrent()) return;

          // Find new paths to add
          const newPaths = normalizedPaths.filter(
            (path) => !keysFromDB.includes(path)
          );
          // Find paths to delete
          const pathsToDelete = keysFromDB.filter(
            (path) => !normalizedPaths.includes(path)
          );

          if (newPaths.length === 0 && pathsToDelete.length === 0) {
            // Render only: an empty or unusable store must not ask the server
            // again from here (that looped /api/bodies back to back).
            await showStoredBodies();
            hideIfCurrent();
            if (isCurrent()) updateLastUpdateTime(eventName, BODY_STORE_NAME);
            return;
          }

          const deletePromises = pathsToDelete.map(async (path) => {
            await deleteImageFromDB(eventName, BODY_STORE_NAME, path);
          });
          await Promise.all(deletePromises);
          if (!isCurrent()) return;

          let complete = true;
          if (newPaths.length > 0) {
            complete = await storeMissingBodies(newPaths, listedThumbs);
            if (!isCurrent()) return;
          }

          // The grid always shows the saved blobs: pointing <img src> back at
          // the server re-requested every thumbnail just stored.
          const dbImages = await showStoredBodies();
          if (!dbImages) return;

          // An incomplete store is not stamped fresh, so the next open (this
          // page or Home) asks the server again instead of trusting it for
          // the refresh interval.
          if (complete) await updateLastUpdateTime(eventName, BODY_STORE_NAME);
        } catch (error) {
          console.error("Error fetching bodies:", error);
          // The list itself failed (e.g. shed with a 503 while the server is
          // busy): show what Home or an earlier visit stored instead of a
          // blank page. Nothing is stamped, so the next open asks again.
          if (isCurrent()) {
            try {
              await showStoredBodies();
            } catch (readError) {
              console.error("Error reading stored bodies:", readError);
            }
          }
        } finally {
          releaseOwn();
          hideIfCurrent();
        }
      } else {
        console.error("BodyGallery - eventName is missing.");
      }
    };

    cleanupPreviousBodyImages();
    loadImages().catch((error) => {
      console.error("Error loading body images:", error);
      if (releaseFetchLoading) releaseFetchLoading();
    });
    hideLoading();
    return () => {
      cancelled = true;
      if (releaseFetchLoading) releaseFetchLoading();
    };
  }, [serverUrl, eventName]);

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      if (goNextReleaseRef.current) goNextReleaseRef.current();
    };
  }, []);

  useEffect(() => {
    setSelectedImages([]);
    setTotalValue(0);
  }, [setSelectedImages, setTotalValue]);

  // Handles body image selection and validates team count compatibility
  const toggleImageSelection = (image) => {
    const imageValue = parseInt(image.split("-").pop().split(".")[0][0]);
    const teamCount = parseInt(image.split("-").pop().split(".")[0][1]);
    const message =
      teamCount === 1
        ? "בחרת תמונה שמתאימה רק ליחיד - אנא שנה את בחירתך בעמוד הקודם או תבחר גוף אחר"
        : "בחרת תמונה שמתאימה רק לזוגי - אנא שנה את בחירתך בעמוד הקודם או תבחר גוף אחר";

    if (teamCount < number) {
      showAlert("warn", message, 6000);
      return;
    }

    if (selectedImages.includes(image)) {
      setSelectedImages(selectedImages.filter((img) => img !== image));
      setTotalValue(totalValue - imageValue);
    } else {
      if (totalValue + imageValue <= number) {
        setSelectedImages([...selectedImages, image]);
        setTotalValue(totalValue + imageValue);
      }
    }
  };

  const goNext = async () => {
    // The download outlives nothing but this event visit: the validated scope
    // is captured at click time, and the shared writes / navigation below only
    // happen while it is still the active one. The overlay is this call's own
    // owner token, so a stale call cannot hide another page's loading.
    const scope = getEventScope();
    const releaseLoading = acquireLoading();
    // Tracked so unmount releases THIS token at once (a same-event Back leaves
    // the download pending); the finally below releases it idempotently.
    goNextReleaseRef.current = releaseLoading;
    const isLive = () => !unmountedRef.current && isCurrentScope(scope);
    try {
      const downloadedBodyImages = {};
      for (const path of selectedImages) {
        try {
          const response = await fetch(`${serverUrl}/image${path}?size=full`);
          if (!response.ok) {
            console.error(
              `Failed to fetch image for path: ${path}. Status: ${response.status}`
            );
            continue;
          }
          const imageBlob = await response.blob();
          if (!isLive()) continue;
          const blobUrl = URL.createObjectURL(imageBlob);
          downloadedBodyImages[path] = blobUrl;
        } catch (error) {
          console.error("Error fetching body image:", error);
        }
      }
      if (!isLive()) {
        Object.values(downloadedBodyImages).forEach((url) =>
          URL.revokeObjectURL(url)
        );
        return;
      }
      sessionStorage.setItem("selectedBodies", JSON.stringify(selectedImages));
      sessionStorage.setItem(
        "bodyImageBlobUrls",
        JSON.stringify(downloadedBodyImages)
      );
      navigate(`/${scope.name}/edit-your-character`);
    } catch (error) {
      console.error("Error in goNext function:", error);
    } finally {
      releaseLoading();
      if (goNextReleaseRef.current === releaseLoading) {
        goNextReleaseRef.current = null;
      }
      if (isLive()) hideLoading();
    }
  };

  const goBack = async () => {
    showLoading();
    navigate(`/${eventName}/face-gallery`);
  };

  return (
    <div className="swingie-bodygallery-container">
      <FlowCountdown />
      <h1 className="swingie-bodygallery-title rtl">בחרו את הדמות שתרצו</h1>
      <ImageGrid
        images={images}
        selectedImages={selectedImages}
        toggleImageSelection={toggleImageSelection}
        totalValue={totalValue}
        number={number}
        serverUrl={serverUrl}
      />
      <div className="swingie-bodygallery-button-container">
        <button className="swingie-bodygallery-button" onClick={goBack}>
          חזרה
        </button>
        <button
          className={`swingie-bodygallery-button ${
            totalValue === number ? "" : "swingie-bodygallery-disabled-button"
          }`}
          onClick={goNext}
          disabled={totalValue !== number}
        >
          <span>הבא </span>
          <span style={{ fontSize: "18px", fontWeight: "normal" }}>
            ({totalValue}/{number})
          </span>
        </button>
      </div>
    </div>
  );
};

export default SwingieBodyGalleryPage;
